import { CoreImageView } from '@core/widgets';
import { useTriggerAction, useUpdateWidgetValue, Loading } from '@libs/core';
import { ActionActionTypeEnum, AssetsSvgMap, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { ReactNode, Suspense, useCallback, useDeferredValue, useState } from 'react';
import { TConsumerInputPhoneData, TConsumerItem, TConsumerListData } from '../../../types/data';
import { TListOption } from '../../../types/options';
import { getHref } from '../../Image/imageUtils';
import InputSearch from '../InputSearch';
import ListItems, { TListItemProps } from '../ListItems';
import styles from '../styles/styles.module.css';
import { filterBySearch } from '../utils/search';

type TListSearchProps = {
    closeModal: () => void;
    renderItem: React.FC<TListItemProps>;
    selectCurrentValue?: (item: TConsumerItem) => ReactNode;
    searchOnServer?: boolean;
    minQueryLength?: number;
} & TWidgetProps<TConsumerListData & TConsumerInputPhoneData, TListOption>;

const _$QUERY = '$query';

const ListSearch = ({ widget, renderItem, selectCurrentValue, closeModal, minQueryLength = 0, searchOnServer = false }: TListSearchProps) => {
    const data = widget.data ?? {};
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<TConsumerItem[]>([]);
    const deferredQuery = useDeferredValue(query);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const setItemsAndMarkSearchValue = useCallback(
        (quer: string, items: TConsumerItem[]) => setItems(quer ? filterBySearch(quer, items, !searchOnServer) : []),
        [searchOnServer],
    );

    const getTextWithQuery = (text?: string, query?: string) => {
        if (text) {
            const queryString = query || '';
            return text.includes(_$QUERY) ? text.replace(_$QUERY, queryString) : `${text} “${queryString}”`;
        }
        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setQuery(val);
        if (searchOnServer) {
            if (val?.length >= minQueryLength) {
                updateWidgetValue(val);
                triggerActionsByType(ActionActionTypeEnum.onUpdateList)
                    .then((w) => {
                        if (Array.isArray(w?.data?.items)) {
                            setItemsAndMarkSearchValue(val, w.data.items);
                        }
                    })
                    .catch((err) => console.log(err));
            } else {
                setItems([]);
            }
        } else {
            setItemsAndMarkSearchValue(val, data.items ?? []);
        }
    };

    return (
        <>
            <InputSearch
                widget={widget}
                asButton={false}
                isSearchInput={true}
                value={query}
                autoFocus={true}
                handleChange={handleChange}
                handleCloseClick={closeModal}
                handleClickRightComponent={() => {
                    setQuery('');
                    setItems([]);
                }}
                backIcon={AssetsSvgMap.ic_chevron_down}
                marginTop={0}
                marginLeft={0}
                marginRight={0}
                marginBottom={32}
            />
            <Suspense fallback={<Loading />}>
                {items?.length > 0 && <ListItems items={items} renderItem={renderItem} />}
                {deferredQuery?.length > minQueryLength && items?.length === 0 && (
                    <div className={styles.notFoundContainer}>
                        <div className={styles.notFoundTextContainer}>
                            <div className={styles.ghostIconWrapper}>
                                <CoreImageView
                                    src={getHref(AssetsSvgMap.ic_ghost)}
                                    styleProps={{
                                        iconColor: `var(--color-grey_72_lc)`,
                                    }}
                                />
                            </div>
                            <h2>{getTextWithQuery(data?.emptySearchResultLabel ?? data.messages?.emptySearchResultLabel, deferredQuery)}</h2>
                        </div>
                        {selectCurrentValue &&
                            selectCurrentValue({
                                id: '',
                                value: deferredQuery,
                            })}
                    </div>
                )}
            </Suspense>
            {selectCurrentValue && deferredQuery && items?.length > 0 && (
                <div className={clsx(styles.notFoundContainer, styles.hasButton)}>
                    <div className={styles.notFoundTextContainer}>
                        <h2>{getTextWithQuery(data?.messages?.noSuggestionsError, deferredQuery)}</h2>
                    </div>
                    {selectCurrentValue({
                        id: '',
                        value: deferredQuery,
                    })}
                </div>
            )}
        </>
    );
};
export default ListSearch;
